import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import get from 'lodash/get';
import useQuery from '../../modules/react/hooks/useQuery';
import useMutation from '../../modules/react/hooks/useMutation';
import { MANAGE } from '../../modules/authorization/actions';
import { BUILDER } from '../../modules/authorization/subjects';
import useCurrentUser from '../hooks/useCurrentUser';
import PublishButton from '../components/PublishButton';
import UndoRedoButtons from '../components/UndoRedoButtons';
import { LAYOUT } from '../components/PublishButton/constants';

const GET_DATA = gql`
  query GetPageLayoutHeaderControls($localeCode: String!, $typeId: ID!, $versionId: ID!) {
    pageLayout(localeCode: $localeCode, typeId: $typeId) {
      id
      versionPublishable(versionId: $versionId)
      previousVersion(versionId: $versionId) {
        id
      }
      nextVersion(versionId: $versionId) {
        id
      }
    }
  }
`;

const PUBLISH_PAGE_LAYOUT = gql`
  mutation PageLayoutVersionPublish($id: ID!, $versionId: ID!) {
    pageLayoutVersionPublish(id: $id, versionId: $versionId) {
      pageLayout {
        id
        versionPublishable(versionId: $versionId)
        previousVersion(versionId: $versionId) {
          id
        }
        nextVersion(versionId: $versionId) {
          id
        }
      }
    }
  }
`;

const propTypes = {
  localeCode: PropTypes.string.isRequired,
  typeId: PropTypes.string.isRequired,
  versionId: PropTypes.string.isRequired,
};

const defaultProps = {};

const PageLayoutHeaderControls = ({ localeCode, typeId, versionId }) => {
  const { data } = useQuery(
    GET_DATA,
    { variables: { localeCode, typeId, versionId }, fetchPolicy: 'network-only', ssr: false },
  );
  const [publishLayout, { loading }] = useMutation(PUBLISH_PAGE_LAYOUT);

  const currentUser = useCurrentUser();
  const unauthorized = currentUser.cannot(MANAGE, BUILDER);

  if (!data) {
    return null;
  }

  const { pageLayout } = data;

  if (!pageLayout) {
    return null;
  }

  const { id: pageLayoutId, versionPublishable } = pageLayout;

  const onClick = () => {
    publishLayout({ variables: { id: pageLayoutId, versionId } });
  };

  const previousVersionId = get(pageLayout, 'previousVersion.id', null);
  const nextVersionId = get(pageLayout, 'nextVersion.id', null);

  const previousVersionPath = (
    previousVersionId
      ? `/admin/locales/${localeCode}/types/${typeId}/page-layout/versions/${previousVersionId}/edit`
      : undefined
  );

  const nextVersionPath = (
    nextVersionId
      ? `/admin/locales/${localeCode}/types/${typeId}/page-layout/versions/${nextVersionId}/edit`
      : undefined
  );

  return (
    <React.Fragment>
      <UndoRedoButtons
        undoDisabled={!previousVersionId}
        redoDisabled={!nextVersionId}
        previousVersionPath={previousVersionPath}
        nextVersionPath={nextVersionPath}
      />
      <PublishButton
        onClick={onClick}
        loading={loading}
        unauthorized={unauthorized}
        disabled={unauthorized || !versionPublishable}
        type={LAYOUT}
      />
    </React.Fragment>
  );
};

PageLayoutHeaderControls.propTypes = propTypes;
PageLayoutHeaderControls.defaultProps = defaultProps;


export default PageLayoutHeaderControls;
